import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Tag, Calendar, User, Hash } from 'lucide-react';

const SegmentSaveForm = ({
  segmentName,
  setSegmentName,
  segmentDescription,
  setSegmentDescription,
  segmentTags,
  setSegmentTags,
  existingSegments = [],
  editingSegment = null,
  validationErrors = [],
  onValidationChange,
  previewData = null
}) => {
  const [nameError, setNameError] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    const allTags = new Set();
    existingSegments.forEach(segment => {
      if (segment.tags && Array.isArray(segment.tags)) {
        segment.tags.forEach(tag => allTags.add(tag));
      }
    });
    setTagSuggestions(Array.from(allTags).sort());
  }, [existingSegments]);

  useEffect(() => {
    if (hasUserInteracted || editingSegment) {
      validateName(segmentName);
    }
  }, [segmentName, existingSegments, editingSegment, hasUserInteracted]);

  const validateName = (name) => {
    let error = '';
    if (!name.trim()) {
      error = 'Segment name is required';
    } else if (name.trim().length < 3) {
      error = 'Segment name must be at least 3 characters';
    } else if (name.trim().length > 50) {
      error = 'Segment name must be less than 50 characters';
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(name.trim())) {
      error = 'Segment name can only contain letters, numbers, spaces, hyphens, and underscores';
    } else if (existingSegments.some(s =>
      s.name.toLowerCase() === name.trim().toLowerCase() &&
      (!editingSegment || s.id !== editingSegment.id)
    )) {
      error = 'A segment with this name already exists';
    }
    setNameError(error);
    if (onValidationChange) {
      onValidationChange('name', error);
    }
  };

  const handleTagInput = (e) => {
    const value = e.target.value;
    setSegmentTags(value);
    if (value.includes(',')) {
      setShowTagSuggestions(false);
    } else {
      const lastTag = value.split(',').pop().trim().toLowerCase();
      setShowTagSuggestions(lastTag.length > 0);
    }
  };

  const addTagSuggestion = (tag) => {
    const lastTagIndex = segmentTags.lastIndexOf(',');
    if (lastTagIndex === -1) {
      setSegmentTags(tag);
    } else {
      const beforeLastTag = segmentTags.substring(0, lastTagIndex + 1);
      setSegmentTags(beforeLastTag + ' ' + tag);
    }
    setShowTagSuggestions(false);
  };

  const getFilteredTagSuggestions = () => {
    const currentTags = segmentTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    const lastTag = segmentTags.split(',').pop().trim().toLowerCase();
    return tagSuggestions
      .filter(tag => tag.toLowerCase().includes(lastTag) && !currentTags.includes(tag.toLowerCase()))
      .slice(0, 5);
  };

  const parsedTags = segmentTags.split(',').map(tag => tag.trim()).filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Segment Name */}
      <div>
        <label htmlFor="segment-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Segment Name <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            id="segment-name"
            type="text"
            value={segmentName}
            onChange={(e) => {
              setSegmentName(e.target.value);
              setHasUserInteracted(true);
            }}
            className={`w-full px-3.5 py-2.5 pr-20 rounded-xl border text-sm transition-colors
              bg-surface dark:bg-dark-700 text-content dark:text-white
              placeholder:text-content-subtle dark:placeholder:text-content-subtle
              focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
              dark:focus:border-blue-400 dark:focus:ring-blue-400/30
              ${nameError
                ? 'border-red-300 dark:border-red-500/60 bg-red-50 dark:bg-red-500/10'
                : 'border-line dark:border-white/[0.15]'
              }`}
            placeholder="Enter a unique segment name"
            maxLength={50}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {nameError ? (
              <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
            ) : segmentName.trim().length >= 3 ? (
              <CheckCircle className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
            ) : null}
            <span className="text-xs text-content-subtle dark:text-gray-600 tabular-nums">
              {segmentName.length}/50
            </span>
          </div>
        </div>
        {nameError && (
          <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            {nameError}
          </p>
        )}
        {!nameError && segmentName.trim().length >= 3 && (
          <p className="mt-1.5 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircle className="w-3 h-3 flex-shrink-0" />
            Name is available
          </p>
        )}
      </div>

      {/* Segment Description */}
      <div>
        <label htmlFor="segment-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Description
        </label>
        <textarea
          id="segment-description"
          value={segmentDescription}
          onChange={(e) => setSegmentDescription(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-white/[0.15]
            bg-surface dark:bg-dark-700 text-sm text-content dark:text-white
            placeholder:text-content-subtle dark:placeholder:text-content-subtle
            focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
            dark:focus:border-blue-400 dark:focus:ring-blue-400/30 resize-none transition-colors"
          placeholder="Optional description of this segment's purpose"
          rows={3}
          maxLength={200}
        />
        <div className="flex justify-between items-center mt-1.5">
          <p className="text-xs text-content-muted dark:text-gray-500">
            Help others understand what this segment is for
          </p>
          <span className="text-xs text-content-subtle dark:text-gray-600 tabular-nums">
            {segmentDescription.length}/200
          </span>
        </div>
      </div>

      {/* Tags */}
      <div className="relative">
        <label htmlFor="segment-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Tags
        </label>
        <div className="relative">
          <input
            id="segment-tags"
            type="text"
            value={segmentTags}
            onChange={handleTagInput}
            onFocus={() => segmentTags.length > 0 && setShowTagSuggestions(true)}
            onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
            className="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-line dark:border-white/[0.15]
              bg-surface dark:bg-dark-700 text-sm text-content dark:text-white
              placeholder:text-content-subtle dark:placeholder:text-content-subtle
              focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
              dark:focus:border-blue-400 dark:focus:ring-blue-400/30 transition-colors"
            placeholder="Enter tags separated by commas (e.g., high-value, nurture, geographic)"
          />
          <Tag className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle dark:text-gray-600 pointer-events-none" />
        </div>

        {/* Tag Suggestions Dropdown */}
        {showTagSuggestions && getFilteredTagSuggestions().length > 0 && (
          <div className="absolute z-20 w-full mt-1.5 rounded-xl border border-line dark:border-white/[0.15] bg-surface dark:bg-[#1e2433] shadow-xl overflow-hidden">
            <div className="px-3 py-2 text-xs font-medium text-content-muted dark:text-gray-500 border-b border-line-subtle dark:border-white/[0.06]">
              Suggested tags
            </div>
            {getFilteredTagSuggestions().map((tag, index) => (
              <button
                key={index}
                onClick={() => addTagSuggestion(tag)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors flex items-center gap-2"
              >
                <Tag className="w-3 h-3 text-content-subtle dark:text-gray-600" />
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Parsed tag pills */}
        {parsedTags.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {parsedTags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium
                  bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400
                  border border-blue-100 dark:border-blue-500/20"
              >
                <Tag className="w-2.5 h-2.5" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <p className="mt-1.5 text-xs text-content-muted dark:text-gray-600">
          Tags help organize and find segments later
        </p>
      </div>

      {/* Metadata Preview */}
      {previewData && (
        <div className="rounded-xl border border-line dark:border-white/[0.15] bg-gray-50 dark:bg-dark-900/60 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-content dark:text-white flex items-center gap-2">
            <Hash className="w-4 h-4 text-content-subtle dark:text-gray-500" />
            Segment Metadata
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-content-subtle dark:text-gray-500 flex-shrink-0" />
              <span className="text-content-muted dark:text-gray-400">Size:</span>
              <span className="font-semibold text-content dark:text-white">{previewData.estimatedSize.toLocaleString()} leads</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-content-subtle dark:text-gray-500 flex-shrink-0" />
              <span className="text-content-muted dark:text-gray-400">Created:</span>
              <span className="font-semibold text-content dark:text-white">{editingSegment ? 'Modified' : 'Now'}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-content-muted dark:text-gray-400">Quality:</span>
              <span className={`font-semibold ${
                previewData.qualityScore >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
                previewData.qualityScore >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {previewData.qualityScore}/100
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-content-muted dark:text-gray-400">Coverage:</span>
              <span className="font-semibold text-content dark:text-white">{previewData.percentage}%</span>
            </div>
          </div>

          {editingSegment && (
            <div className="pt-3 border-t border-line dark:border-white/[0.06] text-xs text-content-muted dark:text-gray-500">
              Original created: {new Date(editingSegment.createdAt).toLocaleDateString()}
              {editingSegment.lastUsed && (
                <span className="ml-2">· Last used: {new Date(editingSegment.lastUsed).toLocaleDateString()}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Validation Summary */}
      {validationErrors.length > 0 && (
        <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-red-800 dark:text-red-300">
              Please fix the following issues:
            </span>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index} className="flex items-start gap-1">
                <span className="mt-0.5">•</span>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SegmentSaveForm;
